// =====================================================
// HOOK: useDashboardStats — couche de données du dashboard stats (RUN 1)
// =====================================================
// Charge une SEULE fois les lignes brutes (crews, prospects, BPI payés,
// pré-inscriptions) + les produits (éditions), puis agrège côté client via la
// couche pure `@/lib/dashboard-stats`. Suit l'idiome usePaymentStats :
// fetchAllPages (contournement cap PostgREST 1000) + useMemo.

import { useEffect, useMemo, useState } from 'react';
import { useSupabase } from './useSupabase';
import { useUsers } from '@/contexts/UsersContext';
import { fetchAllPages } from '@/lib/supabase-paginate';
import { toast } from '@/components/ui';
import {
  computeAppels,
  computeConversion,
  computeFluxCards,
  computeGlobalKpis,
  computeIdHistogram,
  computeOrigin,
  computeProfile,
  computeRomain,
  pickDefaultEdition,
  resolveEditions,
  shiftWindowYears,
  type AppelsData,
  type ConversionData,
  type DateWindow,
  type EditionRef,
  type FluxCardData,
  type GlobalKpis,
  type HistoBucket,
  type OriginData,
  type ProfileData,
  type RawCall,
  type RawCrewMember,
  type RawMemberContact,
  type RawOrigin,
  type RawRows,
  type RawSchool,
  type RomainData,
} from '@/lib/dashboard-stats';
import type { RawProduct } from '@/lib/dashboard-stats/edition';

interface UseDashboardStatsResult {
  loading: boolean;
  editions: EditionRef[];
  kpis: GlobalKpis | null;
  currentEdition: EditionRef | null;
  /** Cartes flux « Entrées de la semaine / du mois » (BPI entrés + PI convertis). */
  flux: { week: FluxCardData; month: FluxCardData } | null;
  /** Histogramme « ID entrées » — deux granularités pré-calculées (bascule instantanée). */
  histogram: { week: HistoBucket[]; day: HistoBucket[] } | null;
  /** Conversion & traçabilité commerciale (RUN 3) : entonnoir + commerciaux + taux édition. */
  conversion: ConversionData | null;
  /** Profil des équipages + métiers/études (RUN 4) — recalculé par segment. */
  profile: ProfileData | null;
  /** Suivi des appels par équipage — buckets 0/1/2/3+ (RUN 5). */
  appels: AppelsData | null;
  /** Origine des PI — visuel barres + récap N-1 (RUN 5). */
  origin: OriginData | null;
  /** Tableau de Romain — mensuel auto + N-1 mois par mois (RUN 6). */
  romain: RomainData | null;
  /** Nombre de lignes chargées (debug / preuve). */
  rawCounts: {
    crews: number;
    prospects: number;
    bpi: number;
    preinscriptions: number;
    crewMembers: number;
  };
}

/**
 * @param editionId édition N sélectionnée (product_id). Si non fournie ou
 *   introuvable, on retombe sur l'édition par défaut (la plus active).
 * @param window plage de dates sélectionnée [début, fin] (RETOUCHE 1). N-1 = la
 *   même plage décalée d'un an (calculée ici via shiftWindowYears).
 * @param now instant de référence (injectable — défaut = maintenant).
 */
export function useDashboardStats(
  editionId: string | null,
  window: DateWindow,
  now: Date = new Date()
): UseDashboardStatsResult {
  const supabase = useSupabase();
  const { users } = useUsers();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<RawRows>({
    crews: [],
    prospects: [],
    bpiPayments: [],
    bpiFormSubmissions: [],
    calls: [],
    crewMembers: [],
    memberContacts: [],
    schools: [],
    origins: [],
  });
  const [products, setProducts] = useState<RawProduct[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      setLoading(true);
      try {
        // Un seul round de fetch parallèle. Read-only, aucun agrégat SQL : les
        // volumes CRM (quelques milliers d'entités) tiennent largement côté client.
        const [
          productsRes,
          formsRes,
          crewsRes,
          prospectsRes,
          bpiRes,
          submissionsRes,
          callsRes,
          crewMembersRes,
          memberContactsRes,
          schoolsRes,
          originsRes,
        ] = await Promise.all([
          fetchAllPages(() => supabase.from('products').select('id, name, start_date, end_date')),
          // Formulaires générant un BPI → pour scoper « PI en attente ».
          fetchAllPages(() =>
            supabase.from('forms').select('id, product_id, generates_bpi').eq('generates_bpi', true)
          ),
          fetchAllPages(() =>
            supabase
              .from('crews')
              // assigned_to = commercial (traçabilité commerciale RUN 3).
              // crew_number/name = libellé relance (RUN 5).
              .select(
                'id, product_id, status, derogation_pending, has_derogation, created_at, assigned_to, crew_number, name'
              )
              .order('created_at', { ascending: true })
              .order('id', { ascending: true })
          ),
          // PR = prospects GLOBAUX (statut prospect, non scopés édition).
          // first_name/last_name = libellé des cibles « 0 appel » (RUN 5).
          fetchAllPages(() =>
            supabase
              .from('contacts')
              .select('id, status, created_at, first_name, last_name')
              .eq('status', 'prospect')
              .order('created_at', { ascending: true })
              .order('id', { ascending: true })
          ),
          // BPI = payment_cases scope=bpi status=paid (on charge tous les bpi non
          // expirés puis on filtre status=paid côté agrégat, pour rester aligné à
          // usePaymentStats et pouvoir étendre aux runs suivants).
          fetchAllPages(() =>
            supabase
              .from('payment_cases')
              // crew_id/contact_id = porteur → rattache le BPI au commercial (RUN 3).
              .select('id, product_id, status, created_at, crew_id, contact_id')
              .eq('scope', 'bpi')
              .neq('status', 'expired')
              .order('created_at', { ascending: true })
              .order('id', { ascending: true })
          ),
          fetchAllPages(() =>
            supabase
              .from('form_submissions')
              .select('id, form_id, converted_at, created_at')
              .order('created_at', { ascending: true })
              .order('id', { ascending: true })
          ),
          // Appels consignés → « Appels / ID » (RUN 3) + suivi des appels par
          // équipage/prospect (RUN 5 : entity_type/entity_id + metadata canal/statut).
          fetchAllPages(() =>
            supabase
              .from('timeline_events')
              .select('id, entity_type, entity_id, created_by, created_at, metadata')
              .eq('event_type', 'call_logged')
              .order('created_at', { ascending: true })
              .order('id', { ascending: true })
          ),
          // Membres d'équipage → profil des équipages (RUN 4).
          fetchAllPages(() =>
            supabase
              .from('crew_members')
              .select('crew_id, contact_id, role')
              .order('crew_id', { ascending: true })
              .order('contact_id', { ascending: true })
          ),
          // Contacts membres : sexe (DÉCLARÉ via le BED, colonne gender — 00230),
          // âge, statut étudiant, métier (champ libre), école.
          // ⚠️ gender/birth_date/school_id peu renseignés tant que les BED ne sont
          // pas remplis (voulu : la vraie donnée sexe vient du BED).
          fetchAllPages(() =>
            supabase
              .from('contacts')
              .select(
                'id, first_name, gender, birth_date, is_not_student, socio_professional_category, school_id, origin_id'
              )
              .order('id', { ascending: true })
          ),
          // Écoles → schools.type = type d'études (best-effort, peu renseigné).
          fetchAllPages(() => supabase.from('schools').select('id, type')),
          // Origines → canal d'acquisition (« Origine des PI », RUN 5).
          fetchAllPages(() => supabase.from('origins').select('id, name, label, color')),
        ]);

        const firstError =
          productsRes.error ||
          formsRes.error ||
          crewsRes.error ||
          prospectsRes.error ||
          bpiRes.error ||
          submissionsRes.error ||
          callsRes.error ||
          crewMembersRes.error ||
          memberContactsRes.error ||
          schoolsRes.error ||
          originsRes.error;
        if (firstError) {
          toast.error('Erreur lors du chargement des statistiques', {
            description: firstError.message,
          });
          return;
        }
        if (cancelled) return;

        // Map form_id → product_id (formulaires BPI) pour scoper les submissions.
        const bpiForms = (formsRes.data ?? []) as { id: string; product_id: string | null }[];
        const formIdSet = new Set(bpiForms.map((f) => f.id));
        const formProductMap = new Map(bpiForms.map((f) => [f.id, f.product_id]));

        const bpiFormSubmissions = (
          (submissionsRes.data ?? []) as {
            id: string;
            form_id: string;
            converted_at: string | null;
            created_at: string;
          }[]
        )
          .filter((s) => formIdSet.has(s.form_id))
          .map((s) => ({
            id: s.id,
            product_id: formProductMap.get(s.form_id) ?? null,
            converted_at: s.converted_at,
            created_at: s.created_at,
          }));

        setProducts((productsRes.data ?? []) as RawProduct[]);
        setRows({
          crews: (crewsRes.data ?? []) as RawRows['crews'],
          prospects: (prospectsRes.data ?? []) as RawRows['prospects'],
          bpiPayments: (bpiRes.data ?? []) as RawRows['bpiPayments'],
          bpiFormSubmissions,
          calls: (callsRes.data ?? []) as RawCall[],
          crewMembers: (crewMembersRes.data ?? []) as RawCrewMember[],
          memberContacts: (memberContactsRes.data ?? []) as RawMemberContact[],
          schools: (schoolsRes.data ?? []) as RawSchool[],
          origins: (originsRes.data ?? []) as RawOrigin[],
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [supabase]);

  const editions = useMemo(() => resolveEditions(products), [products]);

  const crewCountByProduct = useMemo(() => {
    const map: Record<string, number> = {};
    rows.crews.forEach((c) => {
      if (c.product_id) map[c.product_id] = (map[c.product_id] ?? 0) + 1;
    });
    return map;
  }, [rows.crews]);

  const currentEdition = useMemo(() => {
    if (editionId) {
      const found = editions.find((e) => e.id === editionId);
      if (found) return found;
    }
    return pickDefaultEdition(editions, crewCountByProduct);
  }, [editionId, editions, crewCountByProduct]);

  // Fenêtre N-1 = plage sélectionnée décalée d'un an (RETOUCHE 1). Clé stable pour
  // les dépendances de mémo (les bornes en ms, pas l'identité de l'objet window).
  const winStartMs = window.start.getTime();
  const winEndMs = window.end.getTime();
  const prevWindow = useMemo(() => shiftWindowYears(window, -1), [winStartMs, winEndMs]); // eslint-disable-line react-hooks/exhaustive-deps

  const kpis = useMemo(() => {
    if (!currentEdition) return null;
    return computeGlobalKpis({ rows, edition: currentEdition, window, prevWindow });
  }, [rows, currentEdition, window, prevWindow]);

  // Flux d'entrées : cartes momentum (7/30 derniers jours avant la fin de plage)
  // + N-1 = même sous-fenêtre décalée d'un an ; scopées à l'édition N.
  const flux = useMemo(() => {
    if (!currentEdition) return null;
    return computeFluxCards({ rows, edition: currentEdition, window });
  }, [rows, currentEdition, window]);

  // Histogramme ID : les deux granularités pré-calculées (couvrent la plage
  // sélectionnée) → la bascule hebdo/quotidien est instantanée côté composant.
  const histogram = useMemo(() => {
    if (!currentEdition) return null;
    return {
      week: computeIdHistogram({ rows, edition: currentEdition, window, granularity: 'week' }),
      day: computeIdHistogram({ rows, edition: currentEdition, window, granularity: 'day' }),
    };
  }, [rows, currentEdition, window]);

  // Conversion & traçabilité commerciale (RUN 3). Pilotée par la plage + édition.
  // Les noms de commerciaux viennent du cache UsersContext.
  const conversion = useMemo(() => {
    if (!currentEdition) return null;
    const rowsWithUsers: RawRows = {
      ...rows,
      users: users.map((u) => ({ id: u.id, full_name: u.full_name, email: u.email })),
    };
    return computeConversion({ rows: rowsWithUsers, edition: currentEdition, window, prevWindow });
  }, [rows, users, currentEdition, window, prevWindow]);

  // Profil des équipages + métiers/études (RUN 4). RETOUCHE 1 : vue globale
  // scopée à la plage sélectionnée (plus de segment).
  const profile = useMemo(() => {
    if (!currentEdition) return null;
    return computeProfile({ rows, edition: currentEdition, window, now });
  }, [rows, currentEdition, window, now]);

  // Suivi des appels par équipage/prospect (RUN 5). Univers = table de relance,
  // appels comptés en CUMUL (état à date) → indépendant de la plage ;
  // scopé à l'édition N pour les équipages.
  const appels = useMemo(() => {
    if (!currentEdition) return null;
    return computeAppels({ rows, edition: currentEdition });
  }, [rows, currentEdition]);

  // Origine des PI (RUN 5). Scopé édition N + plage sélectionnée (comme le KPI PI).
  const origin = useMemo(() => {
    if (!currentEdition) return null;
    return computeOrigin({ rows, edition: currentEdition, window, prevWindow });
  }, [rows, currentEdition, window, prevWindow]);

  // Tableau de Romain (RUN 6). Mensuel AUTO (une ligne par mois de l'édition N) +
  // N-1 mois par mois. Indépendant du filtre Période (c'est une série temporelle
  // complète, pas une fenêtre glissante) ; scopé à l'édition N.
  const romain = useMemo(() => {
    if (!currentEdition) return null;
    return computeRomain({ rows, edition: currentEdition, now });
  }, [rows, currentEdition, now]);

  const rawCounts = useMemo(
    () => ({
      crews: rows.crews.length,
      prospects: rows.prospects.length,
      bpi: rows.bpiPayments.filter((p) => p.status === 'paid').length,
      preinscriptions: rows.bpiFormSubmissions.length,
      crewMembers: (rows.crewMembers ?? []).length,
    }),
    [rows]
  );

  return {
    loading,
    editions,
    kpis,
    currentEdition,
    flux,
    histogram,
    conversion,
    profile,
    appels,
    origin,
    romain,
    rawCounts,
  };
}
